import axios, { AxiosResponse } from 'axios';
import { firebaseAdminService, FirebaseUserData } from './firebaseAdmin';

// Configuration matching the Streamlit app
// const FLASK_API_URL = "http://localhost:5001/admin";
const FLASK_API_URL = "https://jackbot-widget-backend.onrender.com/admin";

export interface Customer {
  id: string;
  name: string;
  status: string;
  api_key: string;
  context_text?: string;
  widget_config?: WidgetConfig;
  created_at: string;
  role?: 'customer' | 'admin';
  email?: string;
  last_login?: string;
  permissions?: string[];
  firebase_uid?: string;
}

export interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'super_admin';
  status: 'active' | 'inactive';
  created_at: string;
  last_login?: string;
  permissions: string[];
  created_by?: string;
  firebase_uid?: string;
}

export interface UserCreationRequest {
  name: string;
  email?: string;
  role: 'customer' | 'admin';
  permissions?: string[];
  createFirebaseUser?: boolean;
  sendPasswordEmail?: boolean;
}

export interface WidgetConfig {
  apiEndpoint: string;
  model: string;
  title: string;
  subtitle: string;
  primaryColor: string;
  secondaryColor: string;
  welcomeMessage: string;
  temperature: number;
  max_output_tokens: number;
  systemMessage: string;
  enableConversationTracking?: boolean;
}

export const DEFAULT_WIDGET_CONFIG: WidgetConfig = {
  apiEndpoint: `${FLASK_API_URL.replace('/admin', '')}/api/chat`,
  model: "gemini-2.0-flash",
  title: "AI Assistant",
  subtitle: "Powered by Our Service",
  primaryColor: "#4F46E5",
  secondaryColor: "#F3F4F6",
  welcomeMessage: "Hello! I'm your AI assistant. How can I help you today?",
  temperature: 0.3,
  max_output_tokens: 800,
  systemMessage: "You are a helpful, friendly, and concise assistant.",
  enableConversationTracking: true
};

export type ConversationRole = 'user' | 'assistant';

export interface ConversationSummary {
  id: string;
  conversation_id?: string;
  created_at?: string;
  updated_at?: string;
  last_ip?: string;
  user_agent?: string;
  model?: string;
  booking_mode?: boolean;
  used_reservon_tools?: boolean;
  message_count_user?: number;
  message_count_assistant?: number;
  last_user_message_preview?: string;
  last_assistant_message_preview?: string;
}

export interface ConversationMessage {
  id: string;
  role: ConversationRole;
  content: string;
  created_at?: string;
  ip?: string;
  user_agent?: string;
  model?: string;
  booking_mode?: boolean;
  used_reservon_tools?: boolean;
}

export interface ConversationDetail extends ConversationSummary {
  messages?: ConversationMessage[];
}

export interface CustomerStatsResponse {
  customer_id: string;
  lifetime: {
    total_user_messages: number;
    total_assistant_messages: number;
    total_conversations_touched: number;
  };
  recent_scan: {
    scanned_conversations: number;
    active_conversations_last_24h: number;
    latest_conversation_updated_at: string | null;
  };
}

// Default permissions for different roles
export const DEFAULT_PERMISSIONS = {
  customer: [] as string[],
  admin: [
    'view_customers',
    'create_customers', 
    'edit_customers',
    'delete_customers',
    'view_analytics',
    'manage_widget_configs'
  ] as string[],
  super_admin: [
    'view_customers',
    'create_customers',
    'edit_customers', 
    'delete_customers',
    'view_analytics',
    'manage_widget_configs',
    'create_admins',
    'edit_admins',
    'delete_admins',
    'view_system_settings',
    'manage_system_settings'
  ] as string[]
};

// Cache management
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresIn: number;
}

class ApiCache {
  private cache = new Map<string, CacheEntry<any>>();
  private readonly DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes

  set<T>(key: string, data: T, ttl: number = this.DEFAULT_TTL): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      expiresIn: ttl
    });
  }

  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    const isExpired = Date.now() - entry.timestamp > entry.expiresIn;
    if (isExpired) {
      this.cache.delete(key);
      return null;
    }

    return entry.data;
  }

  invalidate(pattern?: string): void {
    if (!pattern) {
      this.cache.clear();
      return;
    }

    const keysToDelete: string[] = [];
    this.cache.forEach((_, key) => {
      if (key.includes(pattern)) {
        keysToDelete.push(key);
      }
    });
    
    keysToDelete.forEach(key => this.cache.delete(key));
  }

  has(key: string): boolean {
    const entry = this.cache.get(key);
    if (!entry) return false;
    
    const isExpired = Date.now() - entry.timestamp > entry.expiresIn;
    if (isExpired) {
      this.cache.delete(key);
      return false;
    }
    
    return true;
  }
}

// Pagination interface
export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

// Request queue for batching
class RequestQueue {
  private queue: Array<() => Promise<any>> = [];
  private processing = false;
  private readonly BATCH_SIZE = 3;
  private readonly DELAY = 100;

  async add<T>(request: () => Promise<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      this.queue.push(async () => {
        try {
          const result = await request();
          resolve(result);
        } catch (error) {
          reject(error);
        }
      });

      this.process();
    });
  }

  private async process(): Promise<void> {
    if (this.processing || this.queue.length === 0) return;

    this.processing = true;

    while (this.queue.length > 0) {
      const batch = this.queue.splice(0, this.BATCH_SIZE);
      
      // Process batch in parallel
      await Promise.allSettled(batch.map(request => request()));
      
      // Small delay between batches to prevent overwhelming the server
      if (this.queue.length > 0) {
        await new Promise(resolve => setTimeout(resolve, this.DELAY));
      }
    }

    this.processing = false;
  }
}

interface ChatMessage {
  role: string;
  content: string;
}
interface TestChatPayload {
  messages: ChatMessage[];
}
class ApiService {
  private baseURL = FLASK_API_URL;
  private cache = new ApiCache();
  private requestQueue = new RequestQueue();

  // Authentication credentials
  public static readonly ADMIN_USERNAME = "jackbot_admin";
  public static readonly ADMIN_PASSWORD = "5<p%90w4{Ejg";

  // ==================== USER MANAGEMENT ====================
  
  // Get all admin users
  async getAllAdminUsers(): Promise<AdminUser[]> {
    const cacheKey = 'admin_users';
    
    const cached = this.cache.get<AdminUser[]>(cacheKey);
    if (cached) {
      return cached;
    }

    return this.requestQueue.add(async () => {
      try {
        const response: AxiosResponse<AdminUser[]> = await axios.get(`${this.baseURL}/admin-users`);
        this.cache.set(cacheKey, response.data, 3 * 60 * 1000); // 3 minutes
        return response.data;
      } catch (error) {
        console.error('Error fetching admin users:', error);
        throw error;
      }
    });
  }

  // Create new user (admin or customer)
  async createUser(userData: UserCreationRequest): Promise<Customer | AdminUser> {
    return this.requestQueue.add(async () => {
      try {
        const permissions = userData.permissions || DEFAULT_PERMISSIONS[userData.role];
        const createFirebaseUser = userData.createFirebaseUser ?? !!userData.email?.trim();
        const sendPasswordEmail = userData.sendPasswordEmail ?? true;

        // If Firebase auth creation is requested, we must have an email
        if (createFirebaseUser && !userData.email?.trim()) {
          throw new Error('Email is required to create a Firebase authentication account.');
        }

        // ====================
        // Customer creation flow
        // ====================
        // For customers, prefer the backend endpoint that creates BOTH:
        // - the customer record (customer_id + api_key)
        // - the Firebase Auth user
        // and returns the firebase_uid, so everything stays consistent.
        if (userData.role === 'customer' && createFirebaseUser && userData.email?.trim()) {
          const created = await this.createCustomerWithFirebase({
            name: userData.name,
            email: userData.email
          });

          if (!created?.firebase_uid) {
            throw new Error('Customer was created but Firebase authentication account was not created (missing firebase_uid).');
          }

          // Ensure claims match what the app expects in AuthContext (role + customerId + permissions)
          await this.setFirebaseUserClaims(created.firebase_uid, {
            role: 'customer',
            permissions,
            customerId: created.customer_id
          });

          // Send password reset email if requested
          if (sendPasswordEmail) {
            await firebaseAdminService.sendPasswordResetEmail(userData.email);
          }

          // Fetch full customer record to return a consistent shape to callers
          const customer = await this.getCustomerById(created.customer_id);

          // Invalidate relevant caches
          this.cache.invalidate('users');
          this.cache.invalidate('admin_users');

          return customer;
        }

        // ====================
        // Admin creation flow
        // ====================
        // Admins are created in the backend (/admin-users) AND optionally get a Firebase Auth user.
        // For Firebase Auth creation we use a secondary auth instance (see firebaseAdminService) to avoid
        // logging out the current admin.
        let firebaseUid: string | undefined;
        let temporaryPassword = '';

        if (createFirebaseUser && userData.email?.trim()) {
          temporaryPassword = firebaseAdminService.generateTemporaryPassword();

          const firebaseUserData: FirebaseUserData = {
            email: userData.email,
            password: temporaryPassword,
            displayName: userData.name,
            role: userData.role,
            permissions
          };

          const firebaseUser = await firebaseAdminService.createFirebaseUser(firebaseUserData);
          firebaseUid = firebaseUser.uid;

          // Set custom claims so AuthContext can reliably identify admins
          await this.setFirebaseUserClaims(firebaseUid, {
            role: 'admin',
            permissions
          });

          // Send password reset email if requested
          if (sendPasswordEmail) {
            await firebaseAdminService.sendPasswordResetEmail(userData.email);
          } else {
            // Helpful fallback for one-time password sharing
            console.log(`🔑 Admin user created with Firebase authentication:
              Email: ${userData.email}
              Temporary Password: ${temporaryPassword}
              Firebase UID: ${firebaseUid}`);
          }
        }

        // Create user in local system (admin-users OR users)
        const endpoint = userData.role === 'admin' ? '/admin-users' : '/users';
        const response: AxiosResponse<Customer | AdminUser> = await axios.post(
          `${this.baseURL}${endpoint}`,
          {
            ...userData,
            permissions,
            firebase_uid: firebaseUid
          }
        );
        
        // Invalidate relevant caches
        this.cache.invalidate('users');
        this.cache.invalidate('admin_users');
        
        return response.data;
      } catch (error) {
        console.error('Error creating user:', error);
        throw error;
      }
    });
  }

  // Update admin user
  async updateAdminUser(userId: string, updates: Partial<AdminUser>): Promise<void> {
    await axios.put(`${this.baseURL}/admin-users/${userId}`, updates);
    
    // Invalidate caches
    this.cache.invalidate(`admin_user_${userId}`);
    this.cache.invalidate('admin_users');
  }

  // Delete admin user
  async deleteAdminUser(userId: string): Promise<void> {
    try {
      // Get user data to find Firebase UID before deletion
      const adminUsers = await this.getAllAdminUsers();
      const userToDelete = adminUsers.find(user => user.id === userId);
      
      // Delete from local system
      await axios.delete(`${this.baseURL}/admin-users/${userId}`);
      
      // Delete from Firebase if Firebase UID exists
      if (userToDelete?.firebase_uid) {
        try {
          await firebaseAdminService.deleteFirebaseUser(userToDelete.firebase_uid);
          console.log('✅ Firebase user deleted successfully:', userToDelete.firebase_uid);
        } catch (firebaseError: any) {
          console.error('❌ Firebase user deletion failed:', firebaseError.message);
          // Continue even if Firebase deletion fails
        }
      }
      
      // Invalidate caches
      this.cache.invalidate(`admin_user_${userId}`);
      this.cache.invalidate('admin_users');
    } catch (error) {
      console.error('Error deleting admin user:', error);
      throw error;
    }
  }

  // Update user role and permissions
  async updateUserRole(userId: string, role: 'customer' | 'admin', permissions?: string[]): Promise<void> {
    await axios.put(`${this.baseURL}/users/${userId}/role`, {
      role,
      permissions: permissions || DEFAULT_PERMISSIONS[role]
    });
    
    // Invalidate caches
    this.cache.invalidate(`customer_${userId}`);
    this.cache.invalidate('users');
    this.cache.invalidate('admin_users');
  }

  // ==================== EXISTING CUSTOMER METHODS ====================
  
  // Lightweight customer data (without context and full widget config)
  async getAllUsersLight(page: number = 1, limit: number = 50): Promise<PaginatedResponse<Omit<Customer, 'context_text' | 'widget_config'>>> {
    const cacheKey = `users_light_${page}_${limit}`;
    
    // Check cache first
    const cached = this.cache.get<PaginatedResponse<Omit<Customer, 'context_text' | 'widget_config'>>>(cacheKey);
    if (cached) {
      return cached;
    }

    return this.requestQueue.add(async () => {
      try {
        const response: AxiosResponse<Customer[]> = await axios.get(
          `${this.baseURL}/users?page=${page}&limit=${limit}`
        );

        console.log(response.data);
        // Strip heavy data for light version
        const lightData = response.data.map(customer => ({
          id: customer.id,
          name: customer.name,
          status: customer.status,
          api_key: customer.api_key,
          created_at: customer.created_at,
          role: customer.role || 'customer',
          email: customer.email,
          context_text: customer.context_text,
        }));

        const result: PaginatedResponse<Omit<Customer, 'context_text' | 'widget_config'>> = {
          data: lightData,
          total: response.data.length,
          page,
          limit,
          hasMore: response.data.length === limit
        };

        // Cache with shorter TTL for frequent updates
        this.cache.set(cacheKey, result, 2 * 60 * 1000); // 2 minutes
        
        return result;
      } catch (error) {
        console.error('Error fetching lightweight users:', error);
        throw error;
      }
    });
  }

  // Full customer data with all details
  async getAllUsers(): Promise<Customer[]> {
    const cacheKey = 'users_full';
    
    // Check cache first
    const cached = this.cache.get<Customer[]>(cacheKey);
    if (cached) {
      return cached;
    }

    return this.requestQueue.add(async () => {
      try {
        const response: AxiosResponse<Customer[]> = await axios.get(`${this.baseURL}/users`);
        
        // Cache full data with longer TTL
        this.cache.set(cacheKey, response.data, 5 * 60 * 1000); // 5 minutes
        
        return response.data;
      } catch (error) {
        console.error('Error fetching users:', error);
        throw error;
      }
    });
  }

  // Get single customer with full data (lazy loaded)
  async getCustomerById(id: string): Promise<Customer> {
    console.log('🔍 [ApiService] getCustomerById called with ID:', id);
    const cacheKey = `customer_${id}`;
    
    // Check cache first
    const cached = this.cache.get<Customer>(cacheKey);
    if (cached) {
      console.log('✅ [ApiService] Found customer in cache:', cached);
      return cached;
    }

    return this.requestQueue.add(async () => {
      try {
        console.log('🔍 [ApiService] Making API call to:', `${this.baseURL}/users/${id}`);
        const response: AxiosResponse<Customer> = await axios.get(`${this.baseURL}/users/${id}`);
        console.log('✅ [ApiService] Customer API response:', response.data);
        
        // Cache individual customer data
        this.cache.set(cacheKey, response.data, 3 * 60 * 1000); // 3 minutes
        
        return response.data;
      } catch (error) {
        console.error(`❌ [ApiService] Error fetching customer ${id}:`, error);
        throw error;
      }
    });
  }

  // Optimized context loading
  async getUserContext(userId: string): Promise<string> {
    console.log('🔍 [ApiService] getUserContext called with userId:', userId);
    const cacheKey = `context_${userId}`;
    
    // Check cache first
    const cached = this.cache.get<string>(cacheKey);
    if (cached) {
      console.log('✅ [ApiService] Found context in cache:', cached);
      return cached;
    }

    return this.requestQueue.add(async () => {
      try {
        console.log('🔍 [ApiService] Making API call to:', `${this.baseURL}/users/${userId}/context`);
        const response: AxiosResponse<{ context_text: string }> = await axios.get(
          `${this.baseURL}/users/${userId}/context`
        );
        console.log('✅ [ApiService] Context API response:', response.data);
        
        const context = response.data.context_text || '';
        console.log('🔍 [ApiService] Extracted context:', context);
        
        // Cache context with longer TTL since it changes less frequently
        this.cache.set(cacheKey, context, 10 * 60 * 1000); // 10 minutes
        
        return context;
      } catch (error) {
        console.error('❌ [ApiService] Error fetching context:', error);
        return '';
      }
    });
  }

  // Batch operations for better performance
  async getMultipleCustomers(ids: string[]): Promise<Customer[]> {
    const results: Customer[] = [];
    const uncachedIds: string[] = [];

    // Check cache for each customer
    for (const id of ids) {
      const cached = this.cache.get<Customer>(`customer_${id}`);
      if (cached) {
        results.push(cached);
      } else {
        uncachedIds.push(id);
      }
    }

    // Fetch uncached customers in parallel
    if (uncachedIds.length > 0) {
      const promises = uncachedIds.map(id => this.getCustomerById(id));
      const fetchedCustomers = await Promise.allSettled(promises);
      
      fetchedCustomers.forEach((result) => {
        if (result.status === 'fulfilled') {
          results.push(result.value);
        }
      });
    }

    return results;
  }

  // Legacy method for backwards compatibility
  async addNewUser(name: string): Promise<void> {
    await this.createUser({
      name,
      role: 'customer'
    });
  }

  async updateUserStatus(userId: string, status: string): Promise<void> {
    await axios.put(`${this.baseURL}/users/${userId}/status`, { status });
    
    // Invalidate specific customer and users list cache
    this.cache.invalidate(`customer_${userId}`);
    this.cache.invalidate('users');
  }

  async updateUserContext(userId: string, context: string): Promise<void> {
    console.log('🔍 [ApiService] updateUserContext called with userId:', userId);
    console.log('🔍 [ApiService] Context to update:', context);
    
    const requestBody = { context_text: context };
    console.log('🔍 [ApiService] Making API call to:', `${this.baseURL}/users/${userId}/context`);
    console.log('🔍 [ApiService] Request body:', requestBody);
    
    await axios.put(`${this.baseURL}/users/${userId}/context`, requestBody);
    console.log('✅ [ApiService] Context update successful');
    
    // Invalidate context and customer cache
    this.cache.invalidate(`context_${userId}`);
    this.cache.invalidate(`customer_${userId}`);
  }

  async updateUserWidgetConfig(userId: string, config: WidgetConfig): Promise<void> {
    console.log('🔍 [ApiService] updateUserWidgetConfig called with userId:', userId);
    console.log('🔍 [ApiService] Widget config to update:', config);
    
    console.log('🔍 [ApiService] Making API call to:', `${this.baseURL}/users/${userId}/widget-config`);
    await axios.put(`${this.baseURL}/users/${userId}/widget-config`, config);
    console.log('✅ [ApiService] Widget config update successful');
    
    // Invalidate customer cache
    this.cache.invalidate(`customer_${userId}`);
  }

  async deleteUser(userId: string): Promise<void> {
    try {
      // Get user data to find Firebase UID before deletion
      const customer = await this.getCustomerById(userId);
      
      // Delete from local system
      await axios.delete(`${this.baseURL}/users/${userId}`);
      
      // Delete from Firebase if Firebase UID exists
      if (customer?.firebase_uid) {
        try {
          await firebaseAdminService.deleteFirebaseUser(customer.firebase_uid);
          console.log('✅ Firebase user deleted successfully:', customer.firebase_uid);
        } catch (firebaseError: any) {
          console.error('❌ Firebase user deletion failed:', firebaseError.message);
          // Continue even if Firebase deletion fails
        }
      }
      
      // Invalidate all related caches
      this.cache.invalidate(`customer_${userId}`);
      this.cache.invalidate(`context_${userId}`);
      this.cache.invalidate('users');
    } catch (error) {
      console.error('Error deleting customer:', error);
      throw error;
    }
  }

  async testCustomerChat(userId: string, payload: TestChatPayload): Promise<{ response: string }> {
    console.log('🔍 [ApiService] testCustomerChat called with userId:', userId);
    console.log('🔍 [ApiService] Payload with full history:', payload);
    
    // The payload is already in the correct format { messages: [...] }, so we send it directly.
    const requestBody = payload;
    
    // const endpoint = `${this.baseURL}/admin/users/${userId}/test-chat`;
    const endpoint = `${this.baseURL}/users/${userId}/test-chat`;
    console.log('🔍 [ApiService] Making API call to:', endpoint);
    console.log('🔍 [ApiService] Request body:', requestBody);
    
    const response: AxiosResponse<{ response: string }> = await axios.post(
      endpoint,
      requestBody
    );
    
    console.log('✅ [ApiService] Chat test response:', response.data);
    return response.data;
  }

  // ==================== CONVERSATIONS + STATS ====================
  async getUserConversations(userId: string, limit: number = 50): Promise<ConversationSummary[]> {
    return this.requestQueue.add(async () => {
      try {
        const safeLimit = Math.max(1, Math.min(limit, 200));
        const response: AxiosResponse<ConversationSummary[]> = await axios.get(
          `${this.baseURL}/users/${userId}/conversations?limit=${safeLimit}`
        );
        return response.data || [];
      } catch (error) {
        console.error('Error fetching conversations:', error);
        throw error;
      }
    });
  }

  async getUserConversation(userId: string, conversationId: string, limit: number = 50): Promise<ConversationDetail> {
    return this.requestQueue.add(async () => {
      try {
        const safeLimit = Math.max(1, Math.min(limit, 200));
        const response: AxiosResponse<ConversationDetail> = await axios.get(
          `${this.baseURL}/users/${userId}/conversations/${conversationId}?limit=${safeLimit}`
        );
        return response.data;
      } catch (error) {
        console.error('Error fetching conversation detail:', error);
        throw error;
      }
    });
  }

  async getUserStats(userId: string, scanLimit: number = 200): Promise<CustomerStatsResponse> {
    return this.requestQueue.add(async () => {
      try {
        const safeLimit = Math.max(1, Math.min(scanLimit, 1000));
        const response: AxiosResponse<CustomerStatsResponse> = await axios.get(
          `${this.baseURL}/users/${userId}/stats?scan_limit=${safeLimit}`
        );
        return response.data;
      } catch (error) {
        console.error('Error fetching user stats:', error);
        throw error;
      }
    });
  }

  // Utility methods
  clearCache(): void {
    this.cache.invalidate();
  }

  preloadCustomer(id: string): void {
    // Preload in background without waiting
    this.getCustomerById(id).catch(() => {
      // Silent fail for preloading
    });
  }

  // Check if data is cached
  isCustomerCached(id: string): boolean {
    return this.cache.has(`customer_${id}`);
  }

  isContextCached(userId: string): boolean {
    return this.cache.has(`context_${userId}`);
  }

  // === NEW USER MANAGEMENT METHODS ===

  // Migrate all existing Firebase users to admin role
  async migrateUsersToAdmin(): Promise<{ migrated_count: number; message: string }> {
    return this.requestQueue.add(async () => {
      try {
        const response: AxiosResponse<{ migrated_count: number; message: string }> = await axios.post(
          `${this.baseURL}/migrate-users-to-admin`
        );
        
        console.log('✅ User migration completed:', response.data.message);
        
        // Invalidate all caches after migration
        this.cache.invalidate('users');
        this.cache.invalidate('admin_users');
        
        return response.data;
      } catch (error) {
        console.error('Error migrating users to admin:', error);
        throw error;
      }
    });
  }

  // Create customer with Firebase authentication
  async createCustomerWithFirebase(userData: {
    name: string;
    email?: string;
    password?: string;
  }): Promise<{
    customer_id: string;
    api_key: string;
    firebase_uid?: string;
    has_firebase_account: boolean;
  }> {
    return this.requestQueue.add(async () => {
      try {
        const response = await axios.post(
          `${this.baseURL}/customers-with-firebase`,
          userData
        );
        
        // Invalidate caches
        this.cache.invalidate('users');
        
        return response.data;
      } catch (error) {
        console.error('Error creating customer with Firebase:', error);
        throw error;
      }
    });
  }

  // Attach existing customer to Firebase user account
  async attachCustomerToFirebase(customerId: string, email: string): Promise<{
    message: string;
    customer_id: string;
    firebase_uid: string;
    email: string;
    updates: string[];
  }> {
    return this.requestQueue.add(async () => {
      try {
        const response = await axios.post(
          `${this.baseURL}/customers/${customerId}/attach-firebase`,
          { email }
        );
        
        // Invalidate caches
        this.cache.invalidate(`customer_${customerId}`);
        this.cache.invalidate('users');
        
        return response.data;
      } catch (error) {
        console.error('Error attaching customer to Firebase:', error);
        throw error;
      }
    });
  }

  // Set Firebase user custom claims
  async setFirebaseUserClaims(firebaseUid: string, claims: {
    role: string;
    permissions?: string[];
    customerId?: string;
  }): Promise<void> {
    try {
      await axios.put(`${this.baseURL}/firebase-users/${firebaseUid}/set-claims`, claims);
      
      // Invalidate caches
      this.cache.invalidate('users');
      this.cache.invalidate('admin_users');
    } catch (error) {
      console.error('Error setting Firebase user claims:', error);
      throw error;
    }
  }

  // Get Firebase user custom claims
  async getFirebaseUserClaims(firebaseUid: string): Promise<{
    firebase_uid: string;
    email: string;
    display_name: string;
    custom_claims: any;
    email_verified: boolean;
    disabled: boolean;
  }> {
    try {
      const response = await axios.get(`${this.baseURL}/firebase-users/${firebaseUid}/get-claims`);
      return response.data;
    } catch (error) {
      console.error('Error getting Firebase user claims:', error);
      throw error;
    }
  }

  // Auto-create customer record for Firebase user (used in auth flow)
  async autoCreateCustomerForFirebaseUser(userData: {
    firebase_uid: string;
    email: string;
    display_name?: string;
  }): Promise<{
    customer_id: string;
    api_key: string;
    firebase_uid: string;
  }> {
    try {
      const response = await axios.post(`${this.baseURL}/auto-create-customer`, userData);
      
      // Invalidate caches
      this.cache.invalidate('users');
      
      return response.data;
    } catch (error) {
      console.error('Error auto-creating customer:', error);
      throw error;
    }
  }
}

export const apiService = new ApiService(); 